import React from 'react';
import { FolderKanban, FileText, GraduationCap, MessageSquarePlus, Sparkles } from 'lucide-react';

interface ActionButtonsProps {
  onAction?: (action: string) => void;
  subjectName?: string | null;
  showEnglishSkills?: boolean;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ onAction, subjectName, showEnglishSkills = false }) => {
  const actionButtons = [
    {
      icon: <FolderKanban className="h-4 w-4" />,
      text: 'Projects',
      color: 'from-cyan-500/20 to-cyan-500/5',
      iconColor: 'text-cyan-300',
      borderColor: 'border-cyan-500/20 hover:border-cyan-500/40',
      action: 'projects',
    },
    {
      icon: <FileText className="h-4 w-4" />,
      text: 'Homework',
      color: 'from-emerald-500/20 to-emerald-500/5',
      iconColor: 'text-emerald-400',
      borderColor: 'border-emerald-500/20 hover:border-emerald-500/40',
      action: 'homework',
    },
    {
      icon: <GraduationCap className="h-4 w-4" />,
      text: 'Revision',
      color: 'from-blue-500/20 to-blue-500/5',
      iconColor: 'text-blue-400',
      borderColor: 'border-blue-500/20 hover:border-blue-500/40',
      action: 'revision',
    },
    ...(showEnglishSkills
      ? [{
          icon: <Sparkles className="h-4 w-4" />,
          text: 'Reading & Composition',
          color: 'from-amber-500/20 to-orange-500/5',
          iconColor: 'text-amber-300',
          borderColor: 'border-amber-500/20 hover:border-amber-500/40',
          action: 'english-skills',
        }]
      : []),
    {
      icon: <MessageSquarePlus className="h-4 w-4" />,
      text: 'Send Feedback',
      color: 'from-violet-500/20 to-violet-500/5',
      iconColor: 'text-violet-400',
      borderColor: 'border-violet-500/20 hover:border-violet-500/40',
      action: 'feedback',
    },
  ];

  const handleClick = (action: string) => {
    if (onAction) {
      onAction(action);
    }
  };

  return (
    <div
      data-tour="action-buttons"
      className={`grid gap-2 sm:gap-3 w-full ${showEnglishSkills ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'}`}
    >
      {actionButtons.map((button, index) => (
        <button
          key={index}
          onClick={() => handleClick(button.action)}
          className={`
            group flex items-center justify-center gap-2 px-4 py-3 w-full
            bg-gradient-to-br ${button.color}
            border ${button.borderColor}
            rounded-xl
            transition-all duration-200
            hover:scale-[1.02] hover:shadow-lg
            active:scale-[0.98]
          `}
        >
          <span className={`${button.iconColor} transition-transform group-hover:scale-110`}>
            {button.icon}
          </span>
          <span className="text-sm font-medium text-[var(--text-primary)]">
            {button.text}
          </span>
        </button>
      ))}
    </div>
  );
};

export default ActionButtons;
